import {
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { faToggleOff, faToggleOn } from '@fortawesome/pro-solid-svg-icons';
import { DirectUpload } from '@rails/activestorage';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { PaymentMethodForm } from 'src/components/PaymentMethodForm';
import { PhoneConsentText } from 'src/components/PhoneConsentText';
import { PhoneInput } from 'src/components/PhoneInput';
import { RadioGroup } from 'src/components/RadioGroup';
import { SignatureCanvas } from 'src/components/SignatureCanvas';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { InlineFileUploader } from 'src/components/Uploader/InlineFileUploader';
import { useSession } from 'src/contexts/SessionContext';
import { fetchExercise } from 'src/models/exercise/exercise';
import { fetchLocations } from 'src/models/location/location';
import { PaymentMethodType } from 'src/models/payment-methods/_types';
import { uploadProgressPhoto } from 'src/models/progress-photo/progress-photo';
import { handleResourceUpload } from 'src/models/resource/resource';
import { handleFileSelect } from 'src/utils/fileHandlers';
import { simpleFormat } from 'src/utils/simpleFormat';

import { SignWaiver } from './SignWaiver';
import {
  CheckOption,
  RepMaxValue,
  UserAssessmentQuestionProps,
  UserAssessmentResultValue,
  WaiverValue,
  WeightMetric,
} from './_types';

export const UserAssessmentQuestion: React.FC<UserAssessmentQuestionProps> = ({
  field,
  index,
  results = [],
  fields = [],
  userAssessment,
  labels = true,
  embedded = false,
  onChange,
  uploadComplete,
  fileAdded: _fileAdded,
  default: isDefault = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'userAssessmentQuestion',
  });
  const { user, session } = useSession();

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () =>
      fetchLocations({
        fetchAll: true,
        sort: 'name',
        sortDir: 'asc',
      }),
    select: (data) => data.locations,
    enabled: field.type === 'location_id',
  });

  const idx = parseInt(String(index), 10);
  const currentResult: UserAssessmentResultValue =
    results.length && idx >= 0
      ? results[user?.id ? idx : idx + 3] || ({} as UserAssessmentResultValue)
      : ({} as UserAssessmentResultValue);

  // Initialize checkbox options
  const initialCheckOptions = (() => {
    if (field.type === 'checkbox' && field.options) {
      return field.options.map((o) => ({
        value: o.label || '',
        points: o.points,
        checked: false,
      }));
    }
    return [];
  })();

  // Initialize values from existing assessment or defaults
  const getInitialValue = () => {
    if (results && userAssessment) {
      if (currentResult.type === 'waiver') {
        return currentResult.value || { checked: null, signature: null };
      }
      return currentResult.value;
    } else {
      // Initialize for new assessment
      if (field.required) {
        if (
          field.type !== 'upload' &&
          field.type !== 'profile_image' &&
          field.value
        ) {
          return field.value;
        }
      }
      if (field.type === 'rep_max' || field.type === 'waiver') {
        return { checked: null, signature: null };
      }
    }
    return null;
  };

  const getInitialWeight = () => {
    if (results && userAssessment && currentResult.type === 'rep_max') {
      const repMaxValue = currentResult.value as RepMaxValue;
      return repMaxValue?.weight || null;
    }
    return null;
  };

  const getInitialReps = () => {
    if (results && userAssessment && currentResult.type === 'rep_max') {
      const repMaxValue = currentResult.value as RepMaxValue;
      return repMaxValue?.reps || null;
    }
    return null;
  };

  const getInitialCheckOptions = () => {
    if (
      results &&
      userAssessment &&
      currentResult.type === 'checkbox' &&
      initialCheckOptions.length &&
      currentResult.value
    ) {
      const valueArray = Array.isArray(currentResult.value)
        ? currentResult.value
        : [currentResult.value];
      return initialCheckOptions.map((o) => ({
        ...o,
        checked: valueArray.includes(o.value),
      }));
    }
    return initialCheckOptions;
  };

  const [value, setValue] = useState<any>(getInitialValue);
  const [weight, setWeight] = useState<number | null>(getInitialWeight);
  const [reps, setReps] = useState<number | null>(getInitialReps);
  const [checkOptions, setCheckOptions] = useState<CheckOption[]>(
    getInitialCheckOptions
  );
  const [cardFormLoaded, setCardFormLoaded] = useState(false);
  const [exercise, setExercise] = useState<any>({});
  const [uploading, setUploading] = useState(false);
  const [, setUploadProgress] = useState(0);
  const [uploadedAsset, setUploadedAsset] = useState<File | null>(null);

  // Calculate if field should be shown based on answer conditions
  const fieldIdx = idx - 1;
  const showField = (() => {
    if (fieldIdx > -1 && !isDefault && fields) {
      const currentField = fields[fieldIdx];
      const result = results[user?.id ? fieldIdx : fieldIdx + 3];
      const answer = currentField?.answer;
      if (answer && result) {
        let resultValue: string[] = [];
        if (typeof result.value === 'string' && result.value.includes(',')) {
          resultValue = result.value.split(',').map((a) => a.trim());
        } else if (typeof result.value === 'string') {
          resultValue = [result.value.trim()];
        } else if (result.value) {
          resultValue = String(result.value)
            .split(',')
            .map((a) => a.trim());
        }

        const answerArray = answer.split(',').map((a) => a.trim());
        const hasOverlap = answerArray.some((a) =>
          resultValue.some((v) => a.toLowerCase() === v.toLowerCase())
        );
        return hasOverlap;
      }
    }
    return true;
  })();

  const useTextArea = [
    'notes',
    'goals',
    'injuries',
    'equipment',
    'textarea',
    'training',
  ].includes(field.type);

  const useTextInput = ['text', 'location', 'height'].includes(field.type);

  const questionClasses = labels
    ? `assessment-field assessment-field-${index}`
    : `custom-assessment-field assessment-field-${index}`;

  const weightMetrics: WeightMetric[] = [
    { name: 'lbs', value: false },
    { name: 'kg', value: true },
  ];

  const points = checkOptions
    .filter((o) => o.checked)
    .reduce((sum, o) => sum + (o.points || 0), 0);

  // Formula evaluation helper
  const evaluateFormula = (
    formula: string,
    resultList: UserAssessmentResultValue[],
    userId?: any
  ): number | null => {
    try {
      const offset = userId ? 0 : 3;
      // Simple formula evaluation - replace variables with values
      let processedFormula = formula;

      // Replace qv{number} with values
      const qvRegex = /qv([0-9]+)/g;
      let matches = qvRegex.exec(formula);
      const vars: any = {};
      while (matches) {
        const result = resultList[parseInt(matches[1], 10) - 1 + offset];
        vars[matches[0]] = result?.value || '';
        matches = qvRegex.exec(formula);
      }

      // Replace q{number} with points or values
      const qRegex = /q([0-9]+)/g;
      matches = qRegex.exec(formula);
      while (matches) {
        const result = resultList[parseInt(matches[1], 10) - 1 + offset];
        vars[matches[0]] =
          result?.points === 0
            ? result.points
            : result?.points || result?.value || 0;
        matches = qRegex.exec(formula);
      }

      // Ensure non-matches get values
      const qvOrQRegex = /qv?([0-9]+)/g;
      matches = qvOrQRegex.exec(formula);
      while (matches) {
        if (!vars[matches[0]]) {
          vars[matches[0]] =
            matches.input && matches.input.includes('v') ? '' : 0;
        }
        matches = qvOrQRegex.exec(formula);
      }

      // Replace variables in formula
      Object.keys(vars).forEach((key) => {
        processedFormula = processedFormula.replace(
          new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
          String(vars[key])
        );
      });

      // Safely evaluate
      const evaluated = Function(
        `'use strict'; return (${processedFormula})`
      )();
      return isNaN(evaluated) ? null : Number(evaluated);
    } catch (error) {
      return null;
    }
  };

  const parsedFormula =
    field.type === 'formula' && field.formula && results.length > 0
      ? evaluateFormula(field.formula, results, user?.id)
      : null;

  // Helper function to trigger onChange with calculated points
  const triggerOnChange = (currentValue: any, currentPoints?: number) => {
    if (!onChange || currentValue === undefined) return;

    let calculatedPoints = currentPoints ?? points;
    if (results.length > 0 && field.pointFormula) {
      const evaluated = evaluateFormula(field.pointFormula, results, user?.id);
      if (evaluated !== null) {
        calculatedPoints = evaluated;
      }
    }
    onChange(field, index, currentValue, calculatedPoints);
  };

  // Initialize async operations on mount
  useEffect(() => {
    // Load exercise if needed
    if (field.exercise) {
      fetchExercise({ id: field.exercise })
        .then((exercise) => {
          setExercise(exercise);
        })
        .catch(() => {
          setExercise({ id: field.exercise, name: 'Exercise Name' });
        });
    }

    // Initialize upload complete for new assessments
    if (!results || !userAssessment) {
      if (
        field.required &&
        (field.type === 'upload' || field.type === 'profile_image')
      ) {
        if (uploadComplete) {
          uploadComplete(
            1,
            null,
            { id: null, image_file_name: null },
            field,
            index
          );
        }
      } else if (field.required && !field.value && onChange) {
        // Trigger initial onChange for required fields without default value
        const currentPoints = currentResult.points ?? undefined;
        let calculatedPoints = currentPoints ?? points;
        if (results.length > 0 && field.pointFormula) {
          const evaluated = evaluateFormula(
            field.pointFormula,
            results,
            user?.id
          );
          if (evaluated !== null) {
            calculatedPoints = evaluated;
          }
        }
        onChange(field, index, currentResult.value, calculatedPoints);
      }
    }
  }, [
    field.type,
    field.required,
    field.value,
    field.exercise,
    field.pointFormula,
    embedded,
    userAssessment?.userId,
    results,
    userAssessment,
    uploadComplete,
    onChange,
    field,
    index,
    currentResult.value,
    currentResult.points,
    points,
    user?.id,
    t,
  ]);

  // Handle formula type value updates when parsedFormula changes
  useEffect(() => {
    if (
      field.type === 'formula' &&
      parsedFormula !== null &&
      !isNaN(parsedFormula)
    ) {
      if (value !== parsedFormula && onChange) {
        setValue(parsedFormula);
        let calculatedPoints = points;
        if (results.length > 0 && field.pointFormula) {
          const evaluated = evaluateFormula(
            field.pointFormula,
            results,
            user?.id
          );
          if (evaluated !== null) {
            calculatedPoints = evaluated;
          }
        }
        onChange(field, index, parsedFormula, calculatedPoints);
      }
    }
  }, [
    field.type,
    parsedFormula,
    value,
    points,
    results,
    field.pointFormula,
    user?.id,
    onChange,
    field,
    index,
  ]);

  // Action handlers
  const handleCardAdded = (result: any) => {
    // Use setupIntent.id (matching Ember behavior where si.id is used)
    const setupIntentId =
      result.setupIntent?.id || result.setupIntent?.payment_method;
    if (setupIntentId) {
      setValue(setupIntentId);
      triggerOnChange(setupIntentId);
    }
  };

  const handleRemoveCard = () => {
    setValue(null);
    setCardFormLoaded(false);
    triggerOnChange(null);
  };

  const handleUpdateInitials = (initials: string[]) => {
    const newValue = { ...value, initials: initials || [] };
    setValue(newValue);
    triggerOnChange(newValue);
  };

  const handleSetGender = (gender: string) => {
    setValue(gender);
    triggerOnChange(gender);
  };

  const handleUpdateSelectValue = (optionValue: string) => {
    const finalValue = optionValue === t('selectAnOption') ? '' : optionValue;
    setValue(finalValue);
    triggerOnChange(finalValue);
  };

  const handleCheckboxChange = (optionIndex: number, checked: boolean) => {
    const updatedOptions = checkOptions.map((opt, idx) =>
      idx === optionIndex ? { ...opt, checked } : opt
    );
    setCheckOptions(updatedOptions);

    // Update value for checkbox type
    if (field.type === 'checkbox') {
      const selectedValues = updatedOptions
        .filter((o) => o.checked)
        .map((o) => o.value);
      setValue(selectedValues);
      triggerOnChange(selectedValues);
    }
  };

  const handleMetricChange = (metricValue: boolean) => {
    const newValue = { ...value, metric: metricValue };
    setValue(newValue);
    triggerOnChange(newValue);
  };

  const handleValueChange = (newValue: any) => {
    setValue(newValue);
    triggerOnChange(newValue);
  };

  // Create upload handler function using model layer upload functions
  const createFileUploadHandler = (): ((file: File) => Promise<string>) => {
    if (!user) {
      throw new Error('User not available');
    }

    return async (file: File): Promise<string> => {
      try {
        if (field.type === 'upload') {
          // Use progress photo upload for 'upload' type
          const result = await uploadProgressPhoto(Number(user.id), file);
          return result.id.toString();
        } else if (field.type === 'file') {
          // Use resource upload for 'file' type
          const result = await handleResourceUpload({ file });
          return result.id.toString();
        } else if (field.type === 'profile_image') {
          // Use resource upload for 'profile_image' type
          const result = await handleResourceUpload({ file });
          return result.id.toString();
        } else {
          const upload = new DirectUpload(file, '/api/v4/direct_uploads', {
            directUploadWillCreateBlobWithXHR: (xhr) => {
              xhr.setRequestHeader('API-TOKEN', user.authToken || '');
            },
          });
          return new Promise<string>((resolve, reject) => {
            upload.create((error, blob) => {
              if (error) {
                setUploading(false);
                reject(error);
              } else if (blob?.signed_id) {
                setUploading(false);
                resolve(blob.signed_id);
              } else {
                setUploading(false);
                reject(new Error('Invalid upload response'));
              }
            });
          });
        }
      } catch (error) {
        throw new Error(`Upload failed: ${error}`);
      }
    };
  };

  const handleFileSelectWrapper = async (file: FileWithPath | null) => {
    if (!file) return;
    setUploadedAsset(file);

    // Call fileAdded callback if provided
    if (_fileAdded) {
      const result = _fileAdded(file);
      if (result) {
        handleValueChange(result);
        return;
      }
    }

    // Handle upload for specific field types
    if (
      field.type === 'upload' ||
      field.type === 'profile_image' ||
      field.type === 'file'
    ) {
      try {
        const uploadHandler = createFileUploadHandler();
        await handleFileSelect({
          selectedFiles: [file],
          setUploadingState: setUploading,
          setProgressState: setUploadProgress,
          handleUpload: async (file: File) => {
            const signedId = await uploadHandler(file);
            return { signed_id: signedId };
          },
          setAssetState: (result: { signed_id: string }) => {
            // Call uploadComplete callback if provided
            if (uploadComplete) {
              uploadComplete(1, file, result, field, index);
            }
            // Set the value to signed_id
            handleValueChange(result.signed_id);
          },
          showFakeProgress: false,
        });
      } catch (error) {
        // Error is already handled by handleFileSelect
      }
    } else {
      // For other types, just set the file
      handleValueChange(file);
    }
  };

  const handleWeightChange = (newWeight: number | null) => {
    setWeight(newWeight);

    // Update value for rep_max type
    if (field.type === 'rep_max') {
      const fieldReps = typeof field.reps === 'number' ? field.reps : undefined;
      const repMaxValue: RepMaxValue = {
        exercise: exercise.id,
        reps: reps || fieldReps,
        weight: newWeight || undefined,
      };
      setValue(repMaxValue);
      triggerOnChange(repMaxValue);
    }
  };

  const handleRepsChange = (newReps: number | null) => {
    setReps(newReps);

    // Update value for rep_max type
    if (field.type === 'rep_max') {
      const fieldReps = typeof field.reps === 'number' ? field.reps : undefined;
      const repMaxValue: RepMaxValue = {
        exercise: exercise.id,
        reps: newReps || fieldReps,
        weight: weight || undefined,
      };
      setValue(repMaxValue);
      triggerOnChange(repMaxValue);
    }
  };

  // Don't render if field is hidden
  if (!showField) {
    return null;
  }

  // Header
  if (field.type === 'header') {
    return (
      <Box className={questionClasses} sx={{ mb: 2 }}>
        <Typography variant="h2">{field.label}</Typography>
      </Box>
    );
  }

  // Instructions
  if (field.type === 'instructions') {
    return (
      <Box className={questionClasses} sx={{ mb: 2 }}>
        <HtmlTypography>
          {simpleFormat(field.label, true, session?.host)}
        </HtmlTypography>
      </Box>
    );
  }

  // Main field rendering
  return (
    <Box className={questionClasses} sx={{ mb: 2 }}>
      <Box sx={{ mb: 1 }}>
        {/* Rep Max Label */}
        {field.type === 'rep_max' && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {field.reps ? `${field.reps} ` : ''}
            {t('repMaxFor')} {exercise.name || ''}
          </Typography>
        )}

        {/* Field Label */}
        {labels && field.type !== 'waiver' && (
          <HtmlTypography sx={{ mb: 1 }}>
            {simpleFormat(field.label, true) + (field.required ? '*' : '')}
          </HtmlTypography>
        )}

        {/* System/Profile fields with weight metric selector */}
        {(field.type === 'system' || field.type === 'profile') &&
          field.stat === 'weight' && (
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid size={{ xs: 8, md: 10 }}>
                <TextField
                  type="number"
                  value={value || ''}
                  onChange={(e) => handleValueChange(e.target.value)}
                  placeholder={t('enterValue')}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 4, md: 2 }}>
                <TextField
                  value={field.metric ? 'kg' : 'lbs'}
                  onChange={(e) => handleMetricChange(e.target.value === 'kg')}
                  options={weightMetrics.map((m) => ({
                    value: m.value ? 'kg' : 'lbs',
                    label: m.name,
                  }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}

        {/* System/Profile fields without weight metric */}
        {(field.type === 'system' || field.type === 'profile') &&
          field.stat !== 'weight' && (
            <Box sx={{ mb: 1 }}>
              <TextField
                type={field.type === 'system' ? 'number' : 'text'}
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder={t('enterValue')}
                fullWidth
              />
            </Box>
          )}

        {/* Text Input */}
        {useTextInput && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              fullWidth
            />
          </Box>
        )}

        {/* Text Area */}
        {useTextArea && (
          <Box sx={{ mb: 1 }}>
            <TextField
              multiline
              rows={4}
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              fullWidth
            />
          </Box>
        )}

        {/* Upload field */}
        {field.type === 'upload' && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={12}>
              <InlineFileUploader
                accept={{ 'video/*': [], 'image/*': [] }}
                maxFileSizeCaption={
                  userAssessment
                    ? t('uploadVideoImageCaptionOverride')
                    : t('uploadVideoImageCaption')
                }
                onFileSelect={handleFileSelectWrapper}
                files={uploadedAsset ? [uploadedAsset] : []}
                multiple={false}
                maxFileSize={10 * 1024 * 1024 * 1024}
                loading={uploading}
              />
            </Grid>
          </Grid>
        )}

        {/* File field */}
        {field.type === 'file' && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid
              size={{
                xs: 12,
                md: value ? 6 : 12,
              }}
            >
              <InlineFileUploader
                accept={{
                  'image/gif': [],
                  'image/jpeg': [],
                  'image/png': [],
                  'video/mp4': [],
                  'video/x-m4v': [],
                  'video/*': [],
                  'text/plain': [],
                  'application/pdf': [],
                }}
                maxFileSizeCaption={
                  userAssessment
                    ? t('uploadFileCaptionOverride')
                    : t('uploadFileCaption')
                }
                onFileSelect={handleFileSelectWrapper}
                files={uploadedAsset ? [uploadedAsset] : []}
                multiple={false}
                maxFileSize={10 * 1024 * 1024 * 1024}
                loading={uploading}
              />
            </Grid>
            {value && !currentResult.mux && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Link href={value} target="_blank" rel="noopener noreferrer">
                  {t('download')}
                </Link>
              </Grid>
            )}
          </Grid>
        )}

        {/* Credit Card field */}
        {field.type === 'credit_card' && (
          <Box sx={{ mb: 1 }}>
            {embedded ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {t('warningCannotEmbed')}
              </Typography>
            ) : (
              <Box>
                <PaymentMethodForm
                  user={user}
                  handlePaymentMethodAdded={handleCardAdded}
                  paymentMethodTypes={
                    field.paymentMethodTypes as PaymentMethodType[]
                  }
                  billingDetails="never"
                  buttonText={
                    value ? t('replaceCreditCard') : t('addCreditCard')
                  }
                  onLoaded={() => setCardFormLoaded(true)}
                />
                {cardFormLoaded && (
                  <>
                    {value ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'success.main' }}
                        >
                          {t('cardAdded')} <Icon icon={faCheckCircle} />
                        </Typography>
                        <Link
                          component="button"
                          onClick={handleRemoveCard}
                          sx={{ cursor: 'pointer' }}
                        >
                          {t('remove')}
                        </Link>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'error.main' }}>
                        {t('cardNotAdded')}{' '}
                        <Icon icon={faExclamationCircle} size="small" />
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Date of Birth field */}
        {field.type === 'dob' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="date"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('enterDate')}
              fullWidth
            />
          </Box>
        )}

        {/* Profile Image field */}
        {field.type === 'profile_image' && (
          <Box sx={{ mb: 1 }}>
            {embedded ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {t('warningCannotEmbed')}
              </Typography>
            ) : (
              <InlineFileUploader
                accept={{ 'image/*': [] }}
                maxFileSizeCaption={
                  userAssessment
                    ? t('uploadFileCaptionOverride')
                    : t('uploadFileCaption')
                }
                onFileSelect={handleFileSelectWrapper}
                files={uploadedAsset ? [uploadedAsset] : []}
                multiple={false}
                maxFileSize={10 * 1024 * 1024 * 1024}
                loading={uploading}
              />
            )}
          </Box>
        )}

        {/* First Name field */}
        {field.type === 'first_name' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('firstName')}
              fullWidth
            />
          </Box>
        )}

        {/* Last Name field */}
        {field.type === 'last_name' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('lastName')}
              fullWidth
            />
          </Box>
        )}

        {/* Phone Number field */}
        {field.type === 'phone_number' && (
          <Box sx={{ mb: 1 }}>
            <PhoneInput
              value={value || ''}
              onChange={(value) => handleValueChange(value)}
              placeholder={t('phoneNumber')}
              fullWidth
            />
            <PhoneConsentText />
          </Box>
        )}

        {/* Street Address field */}
        {field.type === 'street_address' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('streetAddress')}
              fullWidth
            />
          </Box>
        )}

        {/* City field */}
        {field.type === 'city' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('city')}
              fullWidth
            />
          </Box>
        )}

        {/* State field */}
        {field.type === 'state' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('state')}
              fullWidth
            />
          </Box>
        )}

        {/* Zip Code field */}
        {field.type === 'zip_code' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('zipCode')}
              fullWidth
            />
          </Box>
        )}

        {/* Email field */}
        {field.type === 'email' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="email"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('email')}
              fullWidth
            />
          </Box>
        )}

        {/* Location ID field */}
        {field.type === 'location_id' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              options={
                locations?.map((location) => ({
                  value: String(location.id),
                  label: location.name,
                })) || []
              }
              placeholder={t('selectLocation')}
              fullWidth
            />
          </Box>
        )}

        {/* Rep Max field */}
        {field.type === 'rep_max' && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            {!field.reps && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  type="number"
                  label={t('reps')}
                  value={reps || ''}
                  onChange={(e) =>
                    handleRepsChange(parseFloat(e.target.value) || null)
                  }
                  placeholder={t('enterReps')}
                  InputProps={{ inputProps: { min: 0 } }}
                  fullWidth
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: field.reps ? 12 : 6 }}>
              <TextField
                type="number"
                label={t('weight')}
                value={weight || ''}
                onChange={(e) =>
                  handleWeightChange(parseFloat(e.target.value) || null)
                }
                placeholder={t('enterWeight')}
                InputProps={{ inputProps: { min: 0 } }}
                fullWidth
              />
            </Grid>
          </Grid>
        )}

        {/* Password field */}
        {field.type === 'password' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              type="password"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              autoComplete="new-password"
              fullWidth
            />
          </Box>
        )}

        {/* Waiver field */}
        {field.type === 'waiver' && (
          <Box sx={{ mb: 1 }}>
            <Box>
              {field.label && field.label.includes('{{initials}}') ? (
                <SignWaiver
                  text={field.label || t('waiverDefaultLabel')}
                  initials={(value as WaiverValue)?.initials || []}
                  signed={!!(value as WaiverValue)?.signature}
                  onUpdateInitials={handleUpdateInitials}
                />
              ) : (
                <HtmlTypography sx={{ mb: 2 }}>
                  {simpleFormat(field.label || t('waiverDefaultLabel'), true)}
                </HtmlTypography>
              )}
            </Box>
            <Checkbox
              checked={(value as WaiverValue)?.checked || false}
              onChange={(e) => {
                const newValue = {
                  ...value,
                  checked: e.target.checked,
                };
                setValue(newValue);
                triggerOnChange(newValue);
              }}
              label={
                <HtmlTypography>
                  {simpleFormat(field.option || '', true) + '*'}
                </HtmlTypography>
              }
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('waiverSignPrompt')}
            </Typography>
            <SignatureCanvas
              width="100%"
              height="300px"
              updateSignature={(signature) => {
                const newValue = {
                  ...value,
                  signature,
                } as WaiverValue;
                setValue(newValue);
                triggerOnChange(newValue);
              }}
            />
          </Box>
        )}

        {/* Gender field */}
        {field.type === 'gender' && (
          <Box sx={{ mb: 1 }}>
            <RadioGroup
              value={value || ''}
              onChange={(e) => handleSetGender(e.target.value)}
              options={[
                { value: 'Male', label: t('male') },
                { value: 'Female', label: t('female') },
              ]}
              label=""
            />
          </Box>
        )}

        {/* Checkbox field */}
        {field.type === 'checkbox' && (
          <Box sx={{ mb: 1 }}>
            <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
              {checkOptions.map((option, optionIndex) => (
                <Box component="li" key={optionIndex} sx={{ mb: 1 }}>
                  <Checkbox
                    checked={option.checked}
                    onChange={(e) =>
                      handleCheckboxChange(optionIndex, e.target.checked)
                    }
                    label={option.value}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Toggle field */}
        {field.type === 'toggle' &&
          field.options &&
          field.options.length >= 2 && (
            <Box sx={{ mb: 1 }}>
              {labels && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {field.options[0]?.label}
                </Typography>
              )}
              <Button
                onClick={() =>
                  handleUpdateSelectValue(
                    value === field.options?.[0]?.label
                      ? field.options?.[1]?.label || ''
                      : field.options?.[0]?.label || ''
                  )
                }
                sx={{ minWidth: 'auto', p: 1 }}
              >
                <Icon
                  icon={
                    value === field.options[0]?.label ? faToggleOff : faToggleOn
                  }
                />
              </Button>
              {labels && (
                <Typography variant="body2">
                  {field.options[1]?.label}
                </Typography>
              )}
            </Box>
          )}

        {/* Dropdown field */}
        {field.type === 'dropdown' && (
          <Box sx={{ mb: 1 }}>
            <TextField
              value={value || ''}
              onChange={(e) => handleUpdateSelectValue(e.target.value)}
              options={
                field.options
                  ? field.options.map((o) => ({
                      value: o.label,
                      label: o.label,
                    }))
                  : []
              }
              placeholder={
                field.options?.some((o) => o.default)
                  ? undefined
                  : t('selectAnOption')
              }
              fullWidth
            />
          </Box>
        )}

        {/* Formula field */}
        {field.type === 'formula' && parsedFormula !== null && (
          <Box sx={{ mb: 1 }}>
            <HtmlTypography>{String(parsedFormula)}</HtmlTypography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserAssessmentQuestion;
